import { z } from 'zod';
import { valueListItemStatusSchema } from './enums.js';
/**
 * The sync contract.
 *
 * Both directions key on the cursor, which is opaque to the client: a number
 * from a server-side sequence, meaningful only as "everything up to here has
 * been seen".
 */
export const syncCursorSchema = z.coerce.number().int().min(0);
/** One captured article as the device sends it. */
export const itemPushSchema = z.object({
    /** UUID v7 generated on the device. Makes the push idempotent. */
    id: z.uuid(),
    eventId: z.uuid(),
    barcode: z.string().max(200).nullable().default(null),
    supplierItemId: z.uuid().nullable().default(null),
    /** Device time at creation, not server time. */
    clientCreatedAt: z.iso.datetime({ offset: true }),
    /** Values of every designer-created field. */
    attributes: z.record(z.string(), z.unknown()).default({}),
    /** The catalogue version this was captured under. */
    schemaVersion: z.number().int().min(1),
    /** Soft delete, expressed as a push. */
    deleted: z.boolean().default(false),
    /**
     * The revision the device based this change on.
     *
     * Null for a record the server has never seen. A mismatch means someone else
     * changed it first, and the conflict goes back to the user rather than
     * overwriting silently.
     */
    baseRevision: z.number().int().min(1).nullable().default(null),
});
/** A value list entry proposed from the field. */
export const valueListItemPushSchema = z.object({
    id: z.uuid(),
    valueListId: z.uuid(),
    label: z.string().min(1).max(300),
    value: z.string().min(1).max(300).optional(),
    metadata: z.record(z.string(), z.unknown()).default({}),
    deleted: z.boolean().default(false),
    baseRevision: z.number().int().min(1).nullable().default(null),
});
export const syncPushRequestSchema = z.object({
    items: z.array(itemPushSchema).max(500).default([]),
    valueListItems: z.array(valueListItemPushSchema).max(500).default([]),
});
/** What the server made of one pushed record. */
export const pushResultSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('accepted'),
        id: z.uuid(),
        revision: z.number().int(),
        /** Assigned on first sync, e.g. AMB27-0142. Items only. */
        shortCode: z.string().nullable().default(null),
        /**
         * True when a field became mandatory after this record was captured. Not an
         * error — the item is stored and flagged for follow-up.
         */
        needsReview: z.boolean().default(false),
    }),
    z.object({
        status: z.literal('conflict'),
        id: z.uuid(),
        /** The server's current state, so the device can show both sides. */
        serverRevision: z.number().int(),
        serverState: z.record(z.string(), z.unknown()),
    }),
    z.object({
        status: z.literal('rejected'),
        id: z.uuid(),
        /** RFC 9457 shaped, minus the envelope fields the batch already carries. */
        title: z.string(),
        detail: z.string(),
        errors: z.array(z.object({ path: z.string(), message: z.string() })).default([]),
    }),
]);
export const syncPushResponseSchema = z.object({
    items: z.array(pushResultSchema),
    valueListItems: z.array(pushResultSchema),
    /** Cursor covering everything this push produced. */
    cursor: z.number().int(),
});
// --- Pull --------------------------------------------------------------------
export const syncPullQuerySchema = z.object({
    since: syncCursorSchema.default(0),
    /** Maximum records per entity. The response says whether more are waiting. */
    limit: z.coerce.number().int().min(1).max(1000).default(500),
});
export const syncedItemSchema = z.object({
    id: z.uuid(),
    eventId: z.uuid(),
    shortCode: z.string().nullable(),
    barcode: z.string().nullable(),
    supplierItemId: z.uuid().nullable(),
    clientCreatedAt: z.iso.datetime({ offset: true }),
    createdByUserId: z.uuid(),
    attributes: z.record(z.string(), z.unknown()),
    schemaVersion: z.number().int(),
    needsReview: z.boolean(),
    revision: z.number().int(),
    seq: z.number().int(),
    /** Set when the record was deleted; the device removes it locally. */
    deletedAt: z.iso.datetime({ offset: true }).nullable(),
});
export const syncedValueListSchema = z.object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    allowClientCreation: z.boolean(),
    revision: z.number().int(),
    seq: z.number().int(),
    deletedAt: z.iso.datetime({ offset: true }).nullable(),
});
export const syncedValueListItemSchema = z.object({
    id: z.uuid(),
    valueListId: z.uuid(),
    label: z.string(),
    value: z.string(),
    metadata: z.record(z.string(), z.unknown()),
    status: valueListItemStatusSchema,
    /** Points at the survivor; the device resolves the old id through it. */
    mergedIntoId: z.uuid().nullable(),
    sortOrder: z.number().int(),
    revision: z.number().int(),
    seq: z.number().int(),
    deletedAt: z.iso.datetime({ offset: true }).nullable(),
});
export const syncedEventSchema = z.object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    location: z.string().nullable(),
    startsOn: z.string().nullable(),
    endsOn: z.string().nullable(),
    /**
     * The published catalogue version this event captures against, pinned when the
     * event is created. Publishing a new version mid-fair therefore cannot change
     * the form under somebody standing at a booth; an admin lifts the event
     * deliberately, or it stays where it is.
     */
    schemaVersionId: z.uuid().nullable(),
    isActive: z.boolean(),
    revision: z.number().int(),
    seq: z.number().int(),
    deletedAt: z.iso.datetime({ offset: true }).nullable(),
});
/**
 * One field of a published catalogue snapshot.
 *
 * Keyed by `key`, not by id: the snapshot is a frozen copy, and the key is what
 * stays meaningful across versions. A field renamed in the designer keeps its
 * key, so a value captured against version 3 is still readable under version 4.
 */
export const snapshotFieldSchema = z.object({
    key: z.string(),
    label: z.string(),
    type: z.string(),
    system: z.boolean(),
    auto: z.boolean(),
    required: z.boolean(),
    /**
     * Defaulted, because a snapshot published before the flag existed has no such
     * key — and everything back then was offered for capture. Refusing those
     * snapshots would take the sync down for a database that is merely old, which
     * is precisely what happened the first time this schema was strict.
     */
    active: z.boolean().default(true),
    carryOver: z.boolean(),
    helpText: z.string().nullable().default(null),
    group: z.string().nullable().default(null),
    sortOrder: z.number().int(),
    showInList: z.boolean(),
    valueListId: z.uuid().nullable().default(null),
    validation: z.unknown(),
    storage: z.string(),
    columnName: z.string().nullable().default(null),
    defaultValue: z.unknown(),
});
/**
 * A published catalogue version, as the devices receive it.
 *
 * This replaced the live `attribute_definitions` table in the pull payload. That
 * table is what the designer writes to, so an edit that had been saved but
 * deliberately not published still reached every device at the next sync — while
 * the portal said the opposite. Versions are immutable once published, so
 * `revision` never moves and nothing is ever tombstoned.
 */
export const syncedSchemaVersionSchema = z.object({
    id: z.uuid(),
    version: z.number().int(),
    definitions: z.array(snapshotFieldSchema),
    publishedAt: z.iso.datetime({ offset: true }).nullable(),
    revision: z.number().int(),
    seq: z.number().int(),
});
export const syncedPhotoSchema = z.object({
    id: z.uuid(),
    itemId: z.uuid(),
    attributeKey: z.string(),
    sha256: z.string(),
    storageKey: z.string(),
    uploadStatus: z.string(),
    sortOrder: z.number().int(),
    revision: z.number().int(),
    seq: z.number().int(),
    deletedAt: z.iso.datetime({ offset: true }).nullable(),
});
export const syncPullResponseSchema = z.object({
    /**
     * Published catalogue versions. The device builds its form from the one its
     * event points at — see `syncedEventSchema.schemaVersionId`.
     */
    schemaVersions: z.array(syncedSchemaVersionSchema),
    valueLists: z.array(syncedValueListSchema),
    valueListItems: z.array(syncedValueListItemSchema),
    events: z.array(syncedEventSchema),
    items: z.array(syncedItemSchema),
    itemPhotos: z.array(syncedPhotoSchema),
    /**
     * Where to resume.
     *
     * Not simply the highest seq in the response: it never exceeds the point up to
     * which every writing transaction has committed, so a record from a slower
     * transaction cannot be skipped over.
     */
    cursor: z.number().int(),
    /** True when a limit was reached and another pull is worthwhile immediately. */
    hasMore: z.boolean(),
});
// --- Photos ------------------------------------------------------------------
export const photoPresignRequestSchema = z.object({
    /** Device-generated UUID v7, so a retried upload targets the same object. */
    id: z.uuid(),
    itemId: z.uuid(),
    attributeKey: z.string().min(1).max(64),
    /** SHA-256 of the downscaled bytes, lower-case hex. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/, 'SHA-256 muss 64 Hex-Zeichen sein'),
    byteSize: z.number().int().min(1).max(50_000_000),
    contentType: z.string().max(100).default('image/jpeg'),
    width: z.number().int().min(1).nullable().default(null),
    height: z.number().int().min(1).nullable().default(null),
    capturedAt: z.iso.datetime({ offset: true }).nullable().default(null),
    sortOrder: z.number().int().default(0),
});
export const photoPresignResponseSchema = z.object({
    id: z.uuid(),
    /** Null when the same bytes are already stored — nothing left to upload. */
    uploadUrl: z.url().nullable(),
    storageKey: z.string(),
    expiresIn: z.number().int(),
    /** True when deduplication matched and the photo is immediately complete. */
    alreadyUploaded: z.boolean(),
});
export const photoCompleteResponseSchema = z.object({
    id: z.uuid(),
    uploadStatus: z.string(),
    byteSize: z.number().int().nullable(),
    sha256: z.string(),
});
//# sourceMappingURL=sync.js.map